import React, { useEffect, useRef } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader";
import { EXRLoader } from "three/examples/jsm/loaders/EXRLoader";
import * as THREE from "three";

const scenePath = "/models/scene-box-draco.glb";
const HDRI_PATH = "/textures/blocky_photo_studio_4k.exr";

const glassPaneMaterial = new THREE.MeshPhysicalMaterial({
  metalness: 0.9,
  roughness: 0.05,
  envMapIntensity: 0.9,
  clearcoat: 1,
  transparent: true,
  opacity: 0.5,
  reflectivity: 0.2,
  refractionRatio: 0.985,
  ior: 0.9,
  side: THREE.BackSide,
});

const tracked = [];

const getScreenPosition = (object, camera, size) => {
  if (!object || !camera) return { x: 0, y: 0 };

  const vector = new THREE.Vector3();
  object.getWorldPosition(vector);
  vector.project(camera);

  let x = (vector.x * 0.5 + 0.5) * size.width;
  let y = (1 - (vector.y * 0.5 + 0.5)) * size.height;

  if (window.innerWidth < 768) {
    x -= 10;
    y -= 60;
  }

  return {
    name: object.name,
    pos: {
      x: x,
      y: y
    }
  };
};

const LoadGLTF = ({ onLoad, setActiveCamera ,setAreCamerasLoaded }) => {
  const { scene } = useThree();

  useEffect(() => {
    const loader = new GLTFLoader();
    const dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath("https://www.gstatic.com/draco/v1/decoders/");
    loader.setDRACOLoader(dracoLoader);

    loader.load(
      scenePath,
      (gltf) => {
        scene.add(gltf.scene);
        const cameras = [];
        const lights = [];

        gltf.scene.traverse((object) => {
          
          if (object.isCamera) {
            cameras.push(object);
          }
          if (object.isLight) {
            lights.push(object);
          }
          if (object.isMesh) {
            const name = object.name.toLowerCase();  
            if (
              name === 'teamwork-stand' ||
              name === 'problem-solving-stand'  ||
              name === 'critical-thinking-stand'  ||
              name === 'flexibility-stand'  ||
              name === 'javascript-medal'  ||
              name === 'database-medal'  ||
              name === 'docker-medal'  ||
              name === 'git-medal'  ||
              name === 'hadoop-medal'  ||
              name === 'java-medal'  ||
              name === 'ocaml-medal'  ||
              name === 'python-medal' ||
              name === 'bac-frame' ||
              name === 'bachelor-frame' ||
              name === 'licence-frame' ||
              name === 'master-frame' ||
              name === 'record-player-spindle' ||
              name === 'declan-rice' ||
              name === 'headset' ||
              name === 'bottle' ||
              name === 'mac-bg' ||
              name === 'socket-grounding-prong' ||
              name === 'cv' ||
              name === 'phone-display'

            ) { tracked.push(object)};

            if (
              name.includes("glass-pane") ||
              name === "phone-screen001" ||
              name === "tv_screen" ||
              name === "phone-overlay" || 
              name === "window_pane"
            ) {
              object.material = glassPaneMaterial;
            };

          }
        });

        if (cameras.length > 0) {
          onLoad(cameras);
          setAreCamerasLoaded(true);
          setActiveCamera(cameras[0]);
        }
      },
      undefined,
      (error) => console.error("Error loading GLTF:", error)
    );
  }, [scene, onLoad, setActiveCamera, setAreCamerasLoaded]);

  return null;
};

function CustomCamera({ activeCamera, setTrackedObjects, isMobile }) {
  const { set, size } = useThree();
  const cameraRef = useRef();
  const targetPosition = useRef(new THREE.Vector3());
  const targetQuaternion = useRef(new THREE.Quaternion());
  const trackedObjectsRef = useRef([]);

  useEffect(() => {
    if (activeCamera) {
      set({ camera: cameraRef.current });

      // Adjust camera settings for mobile
      if (isMobile) {
        //cameraRef.current.position.set(0, 2, 15); // Move the camera back and up
        cameraRef.current.fov = 40; // Wider field of view for mobile
      } else {
        cameraRef.current.position.copy(activeCamera.position);
        cameraRef.current.fov = activeCamera.fov;
      }

      cameraRef.current.aspect = size.width / size.height;
      cameraRef.current.updateProjectionMatrix();

      targetPosition.current.copy(activeCamera.position);
      targetQuaternion.current.copy(activeCamera.quaternion);
    }
  }, [activeCamera, set, size, isMobile]);

  useFrame(() => {
    if (activeCamera) {
      cameraRef.current.position.lerp(targetPosition.current, 0.05);
      cameraRef.current.quaternion.slerp(targetQuaternion.current, 0.05);
      const trackedObjects = tracked.map((obj) => (getScreenPosition(obj, cameraRef.current, size)));
      trackedObjectsRef.current = trackedObjects;
      setTrackedObjects(trackedObjects);
    }
  });

  useEffect(() => {
    const handleResize = () => {
      if (cameraRef.current) {
        cameraRef.current.aspect = window.innerWidth / window.innerHeight;
        //cameraRef.current.fov = isMobile ? activeCamera?.fov + 90 : activeCamera?.fov;
        //cameraRef.current.zoom = isMobile ? 0.2 : 1;
        cameraRef.current.updateProjectionMatrix();
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isMobile, activeCamera]);

  return (
    <perspectiveCamera
      ref={cameraRef}
      fov={50}
      aspect={size.width / size.height}
      near={0.1}
      far={100}
    />
  );
};

function Environment() {
  const { scene } = useThree();

  useEffect(() => {
    new EXRLoader().load(HDRI_PATH, (texture) => {
      texture.mapping = THREE.EquirectangularReflectionMapping;
      if (scene.environment) scene.environment.dispose();
      if (scene.background) scene.background.dispose();
      scene.environment = texture;
      scene.background = texture;
      scene.environmentIntensity = 0.2;
    });
  }, [scene]);

  return null;
}

const SceneComponent = ({cameras, setCameras, activeCamera, setActiveCamera, setAreCamerasLoaded, setTrackedObjects, isMobile}) => {
  return (
    <div style={{ width: "100vw", height: "100vh", position: "relative"}}>
      <Canvas
        shadows
        onCreated={({ gl }) => {
          gl.setSize(window.innerWidth, window.innerHeight);
          gl.setPixelRatio(window.devicePixelRatio);
          gl.shadowMap.enabled = true;
          gl.outputColorSpace = THREE.SRGBColorSpace;
        }}
      >
        <CustomCamera activeCamera={activeCamera} setTrackedObjects={setTrackedObjects} isMobile={isMobile} />
        <Environment />
        <LoadGLTF onLoad={setCameras} setActiveCamera={setActiveCamera} setAreCamerasLoaded={setAreCamerasLoaded}  />
      </Canvas>
    </div>
  );
};

export default SceneComponent;